import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { personLabel } from '../../components/ApprovalLevels.js';

/**
 * M10 L10.01 — Quality Reviewer Queue.
 *
 * Lists vendor-completion reviews awaiting quality review, filtered by
 * status. Reads from `GET /v1/quality/reviews` (ReviewReadService.dashboard
 * → `{ rows, total }`). Row click opens the scorecard (L10.02) by
 * `reviewId`. Tenant is derived server-side from the JWT (m10CtxFromReq).
 */
interface ReviewRow {
  readonly reviewId: string;
  readonly vendorName: string | null;
  readonly vendorCode: string | null;
  readonly reviewStatus: string;
  readonly slaDeadlineAt: string;
  readonly cycleNo: number;
  readonly decidedAt: string | null;
  /** A completion review has no number of its own; it is known by its job. */
  readonly jobNumber: string | null;
}

interface DashboardResponse {
  readonly rows: readonly ReviewRow[];
  readonly total: number;
}

const STATUS_OPTIONS = [
  '',
  'PENDING',
  'IN_REVIEW',
  'APPROVED',
  'REJECTED',
  'REWORK_REQUESTED',
  'EVIDENCE_REQUESTED',
  'FORWARDED_TO_BO',
  'ESCALATED',
] as const;

export function QualityQueuePage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('');
  const [offset, setOffset] = useState<number>(0);
  const limit = 25;

  const load = useCallback(async () => {
    const qs = new URLSearchParams();
    if (status) qs.set('status', status);
    qs.set('offset', String(offset));
    qs.set('limit', String(limit));
    try {
      const r = await apiRequest<DashboardResponse>(`/v1/quality/reviews?${qs.toString()}`, { token });
      setData({ rows: Array.isArray(r.rows) ? r.rows : [], total: r.total ?? 0 });
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [token, status, offset]);

  useEffect(() => { void load(); }, [load]);

  return (
    <main aria-label="quality-queue">
      <h1 style={{ fontSize: 20, marginBottom: 12 }}>{t('quality.queue.title')}</h1>
      <p style={{ color: 'var(--color-text-muted)', marginBottom: 16, fontSize: 13 }}>
        {t('quality.queue.intro')}
      </p>

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="q-status" style={{ fontSize: 12 }}>{t('quality.common.status')}</label>
          <select id="q-status" aria-label="status-filter" value={status}
                  onChange={(e) => { setStatus(e.target.value); setOffset(0); }}
                  style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
            {STATUS_OPTIONS.map((s) => (
              <option key={s || 'ALL'} value={s}>{s || t('quality.queue.allStatuses')}</option>
            ))}
          </select>
        </div>
      </div>

      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!data ? (
        <p>{t('common.loading')}</p>
      ) : data.rows.length === 0 ? (
        <p data-testid="queue-empty" style={{ color: 'var(--color-text-muted)' }}>{t('quality.queue.empty')}</p>
      ) : (
        <>
          <DataTable<ReviewRow>
            testId="reviews-table"
            ariaLabel="reviews-table"
            rowKey={(r) => r.reviewId}
            rowTestId={(r) => `review-row-${r.reviewId}`}
            columns={[
              { key: 'job', header: t('quality.col.job'), render: (r) => <Link to={`/quality/review/${r.reviewId}`}>{r.jobNumber ?? '—'}</Link> },
              { key: 'vendor', header: t('quality.col.vendor'), render: (r) => personLabel(r.vendorName, r.vendorCode) ?? '—' },
              { key: 'status', header: t('quality.common.status'), render: (r) => r.reviewStatus },
              { key: 'cycle', header: t('quality.col.cycle'), render: (r) => r.cycleNo },
              { key: 'sla', header: t('quality.col.sla'), render: (r) => at(r.slaDeadlineAt) },
            ]}
            rows={data.rows}
          />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
            <Button type="button" disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - limit))}
                    variant="secondary">{t('quality.common.prev')}</Button>
            <span style={{ fontSize: 13 }}>
              {t('quality.queue.pageRange', { from: offset + 1, to: Math.min(offset + limit, data.total), total: data.total })}
            </span>
            <Button type="button" disabled={offset + limit >= data.total}
                    onClick={() => setOffset(offset + limit)}
                    variant="secondary">{t('quality.common.next')}</Button>
          </div>
        </>
      )}
    </main>
  );
}
